import {
  expenseAppliesItself,
  type ImportKind,
  QB_EXPENSE_TYPE_LABELS,
  QB_INVOICE_ISSUE_LABELS,
  type QbExpenseChange,
  type QbExpenseChangeKind,
  type QbInvoiceIssue,
  type QbLinkCounts,
  type QbMatch,
  type QbUnlinked,
  type SyncApplyInput,
  type SyncLinkCounts,
  type SyncPreview,
  type SyncState,
  type SyncStatus,
  type SyncStep,
} from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { errorMessage, get, post } from "../../api";
import { FileCopyStatus } from "../../components/file-copy";
import { HeldExpenses, expenseResultText } from "../../components/qb-expense-sync";
import { LinkQbJobDialog } from "../../components/qb-link-job";
import { InvoiceLinesPanel } from "../../components/invoice-lines";
import { Alert, Badge, Button, Checkbox, Loading, PageHeader, Panel, PanelHeader, cx, formatCents, formatDate, formatDateTime } from "../../components/ui";
import { useSettings } from "./useSettings";

type SyncResponse = { state: SyncState; status: SyncStatus };

const KIND_LABELS: Record<ImportKind, [string, string]> = { contact: ["contact", "contacts"], job: ["job", "jobs"], note: ["note", "notes"], task: ["open task", "open tasks"] };
const CHANGE_LABEL: Record<QbExpenseChangeKind, string> = { add: "New", update: "Changed", move: "Moved to another job", to_general: "Now a general expense", delete: "Deleted in QuickBooks" };
const CHANGE_ORDER: QbExpenseChangeKind[] = ["delete", "to_general", "move", "add", "update"];
const LINK_GROUP: Record<QbMatch["kind"], string> = { job: "Jobs", customer: "Customers", vendor: "Vendors and subs" };
const linkKey = (m: { kind: string; recordId: string; qbId: string }) => `${m.kind}:${m.recordId}:${m.qbId}`;
const plural = (n: number, [one, many]: [string, string]) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

/**
 * Settings → Sync: the one place to bring the CRM up to date with QuickBooks (and JobNimbus, until
 * we're done with it: then nothing of JobNimbus shows). Sync now reads (nothing changes); the review
 * shows everything it would do; Apply does what's ticked, in order.
 */
export function SyncSettings() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["sync"],
    queryFn: () => get<SyncResponse>("/api/sync"),
    refetchInterval: (x) => (x.state.data && ["checking", "applying"].includes(x.state.data.state.phase) ? 1500 : false),
  });
  const set = (r: { state: SyncState }) => qc.setQueryData<SyncResponse>(["sync"], (old) => (old ? { ...old, state: r.state } : old));
  const check = useMutation({ mutationFn: () => post<{ state: SyncState }>("/api/sync/check"), onSuccess: set });
  const apply = useMutation({ mutationFn: (input: SyncApplyInput) => post<{ state: SyncState }>("/api/sync/apply", input), onSuccess: set });
  const phase = q.data?.state.phase;
  // Once Apply finishes, what it changed shows everywhere.
  useEffect(() => {
    if (phase === "done") void qc.invalidateQueries({ predicate: (x) => x.queryKey[0] !== "sync" });
  }, [phase, qc]);
  if (q.isLoading || !q.data) return <Loading />;
  const { state, status } = q.data;
  const running = state.phase === "checking" || state.phase === "applying";
  const jnOn = status.jobnimbus.setUp && !status.jobnimbus.retired;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Sync"
        description={
          jnOn
            ? "Brings the CRM up to date with JobNimbus and QuickBooks. Sync now only reads both; you see everything it would change, and nothing changes until you click Apply. Both are only read: nothing is sent to JobNimbus or QuickBooks."
            : "Brings the CRM up to date with QuickBooks, the source of truth for money. Sync now only reads QuickBooks; you see everything it would change, and nothing changes until you click Apply. Anything deleted in QuickBooks is voided here."
        }
        action={
          <Button variant="primary" busy={check.isPending || state.phase === "checking"} disabled={running} onClick={() => check.mutate()}>
            Sync now
          </Button>
        }
      />
      <StatusBlock status={status} />
      {(check.error || apply.error) && <Alert>{errorMessage(check.error ?? apply.error)}</Alert>}
      {state.phase !== "idle" && <Steps state={state} jnOn={jnOn} />}
      {state.phase === "ready" && state.preview && <Review preview={state.preview} jnInUse={jnOn} applying={apply.isPending} onApply={(input) => apply.mutate(input)} />}
      {state.phase === "done" && state.applied && <Applied state={state} />}
      {status.quickbooks.held > 0 && <HeldExpenses />}
      {jnOn && <FileCopyStatus />}
      {jnOn && <JobNimbusRetired status={status} />}
    </div>
  );
}

function StatusBlock({ status }: { status: SyncStatus }) {
  const jn = status.jobnimbus;
  const jnOn = jn.setUp && !jn.retired;
  const qb = status.quickbooks;
  const row = (label: string, value: string) => (
    <div className="flex flex-wrap justify-between gap-x-3 py-1">
      <span className="text-muted">{label}</span>
      <span>{value}</span>
    </div>
  );
  return (
    <Panel>
      <div className={cx("grid gap-5 px-5 py-4 text-sm", jnOn && "sm:grid-cols-2")}>
        {jnOn && (
          <div>
            <div className="mb-1 font-semibold">JobNimbus</div>
            {row("Last read", formatDateTime(jn.lastPull))}
            {row("Last added", formatDateTime(jn.lastAdded))}
            {row("Invoices brought in", jn.invoicesBroughtIn.toLocaleString("en-US"))}
          </div>
        )}
        <div>
          <div className="mb-1 flex items-center gap-2 font-semibold">
            QuickBooks {qb.connected ? <Badge tone="ok">Connected</Badge> : <Badge tone="off">Not connected</Badge>}
          </div>
          {row("Change notices", qb.webhooks ? (qb.lastNotice ? `On · last ${formatDateTime(qb.lastNotice)}` : "On · none yet") : "Not set up (hourly instead)")}
          {row("Changes last read", formatDateTime(qb.lastChanges))}
          {row("Last full check", formatDateTime(qb.lastMorningSync))}
        </div>
      </div>
      {qb.linkCounts && (
        <div className="border-t border-mist px-5 py-3 text-sm">
          <div className="mb-2 flex flex-wrap justify-between gap-x-3">
            <span className="font-semibold">Linked to QuickBooks</span>
            <span className="text-xs text-muted">As of {formatDateTime(qb.linkCounts.checkedAt)}</span>
          </div>
          <LinkCounts counts={qb.linkCounts} />
        </div>
      )}
      <p className="border-t border-mist px-5 py-3 text-xs text-muted">
        On its own: QuickBooks's changes come in within a minute of its notice (hourly otherwise), and a full check runs every morning. Single changes apply on their own, and what's deleted in QuickBooks is voided here; more than 25 at once wait for you here.
      </p>
      {status.warnings.length > 0 && (
        <div className="flex flex-col gap-2 px-5 pb-4">
          {status.warnings.map((w) => (
            <Alert key={w} tone="info">
              {w}
            </Alert>
          ))}
        </div>
      )}
    </Panel>
  );
}

const STEP_TONE: Record<SyncStep["status"], string> = { waiting: "text-muted", running: "text-brand", done: "text-ok", skipped: "text-muted", failed: "text-danger" };
const STEP_ICON: Record<SyncStep["status"], string> = { waiting: "○", running: "◐", done: "✓", skipped: "–", failed: "✕" };

function Steps({ state, jnOn }: { state: SyncState; jnOn: boolean }) {
  const title = state.phase === "checking" ? (jnOn ? "Reading JobNimbus and QuickBooks…" : "Reading QuickBooks…") : state.phase === "applying" ? "Applying…" : state.phase === "ready" ? "Ready to review" : state.phase === "done" ? "Applied" : "Stopped";
  return (
    <Panel>
      <PanelHeader title={title} description={state.finishedAt ? `${state.phase === "ready" ? "Checked" : "Finished"} ${formatDateTime(state.finishedAt)}.` : state.startedAt ? `Started ${formatDateTime(state.startedAt)}. You can leave this page; it keeps going.` : undefined} />
      <ul className="flex flex-col gap-1 px-5 py-4 text-sm">
        {state.steps.map((s) => (
          <li key={s.key} className="flex flex-wrap gap-x-2">
            <span className={cx("w-4 font-bold", STEP_TONE[s.status])}>{STEP_ICON[s.status]}</span>
            <span className={s.status === "skipped" ? "text-muted" : ""}>{s.label}</span>
            {s.detail && <span className={s.status === "failed" ? "text-danger" : "text-muted"}>· {s.detail}</span>}
          </li>
        ))}
      </ul>
      {state.error && (
        <div className="px-5 pb-4">
          <Alert>{state.error}</Alert>
        </div>
      )}
    </Panel>
  );
}

function Review({ preview, jnInUse, applying, onApply }: { preview: SyncPreview; jnInUse: boolean; applying: boolean; onApply: (input: SyncApplyInput) => void }) {
  const jn = preview.jobnimbus;
  const qb = preview.quickbooks;
  const held = useQuery({ queryKey: ["qb-held"], queryFn: () => get<{ id: string }[]>("/api/integrations/quickbooks/expenses/held") });
  const heldIds = useMemo(() => new Set((held.data ?? []).map((h) => h.id)), [held.data]);
  // Deletions held for approval have their own card (Delete them / Keep them).
  const allChanges = (qb?.expenses.changes ?? []).filter((c) => !(c.kind === "delete" && c.receiptId && heldIds.has(c.receiptId)));
  // On linked jobs: QuickBooks's own job tag places them, so they come in without a tick.
  const onJobs = allChanges.filter(expenseAppliesItself);
  const changes = allChanges.filter((c) => !expenseAppliesItself(c));
  const [jnOn, setJnOn] = useState(true);
  const [notFoundOf, setNotFoundOf] = useState<QbUnlinked["kind"] | "">("");
  const [linksOff, setLinksOff] = useState<Set<string>>(new Set());
  const [changesOff, setChangesOff] = useState<Set<string>>(new Set());
  const jnTotal = jn ? Object.values(jn.adding).reduce((a, b) => a + b, 0) : 0;
  const links = (qb?.links ?? []).filter((m) => !linksOff.has(linkKey(m)));
  const keys = changes.filter((c) => !changesOff.has(c.key)).map((c) => c.key);
  const attention = (qb?.notFound.length ?? 0) + (qb?.invoiceIssues.length ?? 0) + (qb?.expenses.attention.length ?? 0);
  const nothing = !jnTotal && !qb?.links.length && !allChanges.length;
  return (
    <>
      {jn && (
        <Panel>
          <PanelHeader title="From JobNimbus" description="New records since the last time, and anything changed that hasn't come over yet. Records already added are left alone. After adding: subs' paperwork, job titles, JobNimbus's invoices, estimates, and payments, and new files." />
          <div className="flex flex-col gap-2 px-5 py-4 text-sm">
            {jnTotal ? (
              <Checkbox
                label={`Add ${(Object.keys(KIND_LABELS) as ImportKind[]).filter((k) => jn.adding[k]).map((k) => plural(jn.adding[k], KIND_LABELS[k])).join(", ")}`}
                description="Then refresh invoices, paperwork, and files from JobNimbus."
                checked={jnOn}
                onChange={setJnOn}
              />
            ) : (
              <Checkbox label="Nothing new to add" description="Still refresh invoices, estimates, payments, paperwork, and files from JobNimbus." checked={jnOn} onChange={setJnOn} />
            )}
            {jn.flagged > 0 && (
              <Alert tone="info">
                {jn.flagged} of these {jn.flagged === 1 ? "was" : "were"} marked for a look (a possible duplicate, someone already in the CRM, no customer…).{" "}
                <Link to="/settings/import" className="font-semibold underline">
                  Look at them
                </Link>{" "}
                to change what's added; then come back and Apply.
              </Alert>
            )}
          </div>
        </Panel>
      )}
      {qb && jnInUse && (
        <Panel>
          <PanelHeader title="How linking stands" description="Before this Apply. Ready: linked when you Apply with them ticked. Not found: click to see which, and why, under Needs attention." />
          <div className="px-5 py-4 text-sm">
            <LinkCounts
              counts={qb.counts}
              onNotFound={(k) => {
                setNotFoundOf(k);
                document.getElementById("needs-attention")?.scrollIntoView({ behavior: "smooth" });
              }}
            />
          </div>
        </Panel>
      )}
      {qb && (
        <Panel>
          <PanelHeader title={`Links to QuickBooks (${qb.links.length})`} description="Records here matched to what's already in QuickBooks, with every reason they agree. Linking changes only the CRM. Untick any that look wrong." />
          <div className="px-3 py-2 text-sm">
            {!qb.links.length ? (
              <p className="px-2 py-2 text-muted">Nothing new to link.</p>
            ) : (
              <TickList
                groups={(["job", "customer", "vendor"] as const).map((k) => ({ title: LINK_GROUP[k], items: qb.links.filter((m) => m.kind === k) }))}
                keyOf={linkKey}
                off={linksOff}
                setOff={setLinksOff}
                render={(m) => ({ label: `${m.label} → ${m.qbName}`, description: `${m.how} (QuickBooks #${m.qbId})` })}
              />
            )}
          </div>
        </Panel>
      )}
      {qb && (
        <Panel>
          <PanelHeader title={`Expenses from QuickBooks (${allChanges.length.toLocaleString("en-US")})`} description="Bills, expenses, checks, card charges, and vendor credits on linked jobs, and this year's general expenses, as receipts. Deleted in QuickBooks means void here (shown on its job, counted nowhere)." />
          <div className="px-3 py-2 text-sm">
            {onJobs.length > 0 && (
              <p className="px-2 py-2 text-ink-soft">
                <span className="font-semibold text-ink">{plural(onJobs.length, ["new or changed expense", "new or changed expenses"])} on linked jobs</span> come in on their own: QuickBooks has them on those jobs. If a link is wrong, fix the link and the next sync moves its expenses.
              </p>
            )}
            {!allChanges.length ? (
              <p className="px-2 py-2 text-muted">Everything matches QuickBooks.</p>
            ) : !changes.length ? null : (
              <TickList
                groups={expenseGroups(changes)}
                keyOf={(c) => c.key}
                off={changesOff}
                setOff={setChangesOff}
                render={changeText}
              />
            )}
          </div>
        </Panel>
      )}
      {qb && attention > 0 && <NeedsAttention qb={qb} only={notFoundOf} setOnly={setNotFoundOf} />}
      {jnInUse && <InvoiceLinesPanel />}
      <Panel className="sticky bottom-3 z-10">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
          <span className="text-ink-soft">
            {nothing && !jn
              ? "Nothing to change."
              : [jn && jnOn && (jnTotal ? `JobNimbus: add ${jnTotal.toLocaleString("en-US")}` : "JobNimbus: refresh"), qb && `${links.length} of ${qb.links.length} links`, qb && onJobs.length > 0 && `${onJobs.length.toLocaleString("en-US")} expenses on jobs`, qb && changes.length > 0 && `${keys.length.toLocaleString("en-US")} of ${changes.length.toLocaleString("en-US")} to review`].filter(Boolean).join(" · ")}
          </span>
          <Button variant="primary" busy={applying} disabled={!(jn && jnOn) && !links.length && !keys.length && !qb} onClick={() => onApply({ jobnimbus: !!jn && jnOn, links: links.map((m) => ({ kind: m.kind, recordId: m.recordId, qbId: m.qbId })), expenseKeys: keys })}>
            Apply
          </Button>
        </div>
      </Panel>
    </>
  );
}

function changeText(c: QbExpenseChange) {
  const where = c.kind === "move" ? `${c.fromJob} → ${c.toJob}` : c.kind === "to_general" ? `off ${c.fromJob}` : (c.toJob ?? c.fromJob) || (c.kind === "add" ? "general expense" : null);
  return {
    label: `${c.vendorName || QB_EXPENSE_TYPE_LABELS[c.qbType]} · ${formatCents(c.amountCents)}`,
    description: [QB_EXPENSE_TYPE_LABELS[c.qbType], c.date && formatDate(c.date), c.source === "crm" && "entered here", where].filter(Boolean).join(" · "),
  };
}

/** What to review: deletions and moves first, then this year's general expenses a month at a time (folded). */
function expenseGroups(changes: QbExpenseChange[]) {
  const general = changes.filter((c) => c.kind === "add");
  const months = [...new Set(general.map((c) => c.date?.slice(0, 7) ?? ""))].sort().reverse();
  const monthName = (m: string) => (m ? new Date(`${m}-15T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "No date");
  return [
    ...CHANGE_ORDER.filter((k) => k !== "add").map((k) => ({ title: CHANGE_LABEL[k], items: changes.filter((c) => c.kind === k) })),
    ...months.map((m) => ({ title: `New general expenses · ${monthName(m)}`, items: general.filter((c) => (c.date?.slice(0, 7) ?? "") === m), folded: true })),
  ];
}

/** Grouped checkboxes, all ticked to start, with tick/untick per group. */
function TickList<T>({ groups, keyOf, off, setOff, render }: { groups: { title: string; items: T[]; folded?: boolean }[]; keyOf: (t: T) => string; off: Set<string>; setOff: (s: Set<string>) => void; render: (t: T) => { label: string; description: string } }) {
  // Folded groups (each month of general expenses) show only their heading until opened.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (keys: string[], on: boolean) => {
    const next = new Set(off);
    for (const k of keys) on ? next.delete(k) : next.add(k);
    setOff(next);
  };
  return (
    <ul className="max-h-[32rem] overflow-auto">
      {groups
        .filter((g) => g.items.length)
        .flatMap((g) => {
          const keys = g.items.map(keyOf);
          const anyOn = keys.some((k) => !off.has(k));
          const ticked = keys.filter((k) => !off.has(k)).length;
          const shown = !g.folded || open.has(g.title);
          return [
            <li key={`h-${g.title}`} className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-mist bg-white px-2 py-1.5 text-xs font-semibold tracking-wide text-muted uppercase">
              {g.folded ? (
                <button
                  className="text-left uppercase hover:text-ink"
                  aria-expanded={shown}
                  onClick={() => {
                    const next = new Set(open);
                    shown ? next.delete(g.title) : next.add(g.title);
                    setOpen(next);
                  }}
                >
                  {shown ? "▾" : "▸"} {g.title} ({ticked === keys.length ? keys.length : `${ticked} of ${keys.length}`})
                </button>
              ) : (
                <span>
                  {g.title} ({g.items.length})
                </span>
              )}
              <button className="font-semibold normal-case text-brand hover:underline" onClick={() => toggle(keys, !anyOn)}>
                {anyOn ? "Untick these" : "Tick these"}
              </button>
            </li>,
            ...(shown ? g.items : []).map((t) => {
              const k = keyOf(t);
              const r = render(t);
              return (
                <li key={k} className="border-b border-mist last:border-0">
                  <Checkbox label={r.label} description={r.description} checked={!off.has(k)} onChange={(v) => toggle([k], v)} />
                </li>
              );
            }),
          ];
        })}
    </ul>
  );
}

const KIND_TITLES: [keyof SyncLinkCounts, QbUnlinked["kind"], string][] = [
  ["jobs", "job", "Jobs"],
  ["customers", "customer", "Customers"],
  ["vendors", "vendor", "Vendors and subs"],
];
const NOT_FOUND_LABEL: Record<QbUnlinked["kind"], string> = { job: "jobs", customer: "customers", vendor: "vendors and subs" };

/** Jobs, customers, and vendors: how many are linked to QuickBooks, ready to link, and not found. */
function LinkCounts({ counts, onNotFound }: { counts: SyncLinkCounts; onNotFound?: (k: QbUnlinked["kind"]) => void }) {
  return (
    <div className="grid gap-2 sm:grid-cols-3">
      {KIND_TITLES.map(([key, kind, title]) => {
        const c: QbLinkCounts = counts[key];
        return (
          <div key={key} className="rounded-[var(--radius-control)] border border-line p-3">
            <div className="font-semibold">{title}</div>
            <div className="text-2xl font-bold">
              {c.linked.toLocaleString("en-US")} <span className="text-sm font-medium text-muted">of {c.total.toLocaleString("en-US")} linked</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 text-xs">
              {c.ready > 0 && <span className="text-ok">{c.ready.toLocaleString("en-US")} ready to link</span>}
              {c.notFound > 0 &&
                (onNotFound ? (
                  <button className="font-semibold text-wait hover:underline" onClick={() => onNotFound(kind)}>
                    {c.notFound.toLocaleString("en-US")} not found
                  </button>
                ) : (
                  <span className="text-wait">{c.notFound.toLocaleString("en-US")} not found</span>
                ))}
              {c.total > 0 && c.ready === 0 && c.notFound === 0 && <span className="text-ok">All linked</span>}
            </div>
            {!!c.notNeeded && (
              <div className="mt-1 text-xs text-muted">
                {kind === "customer"
                  ? `${c.notNeeded.toLocaleString("en-US")} customers with no job at Sold yet aren't counted; they go to QuickBooks when one is sold.`
                  : `${c.notNeeded.toLocaleString("en-US")} earlier jobs (before Sold) aren't counted; they go to QuickBooks when they're sold.`}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function NeedsAttention({ qb, only, setOnly }: { qb: NonNullable<SyncPreview["quickbooks"]>; only: QbUnlinked["kind"] | ""; setOnly: (k: QbUnlinked["kind"] | "") => void }) {
  const qc = useQueryClient();
  const [linking, setLinking] = useState<QbUnlinked | null>(null);
  const [done, setDone] = useState<Set<string>>(new Set());
  const link = useMutation({
    mutationFn: (i: QbInvoiceIssue) => post(`/api/jobs/${i.jobId}/quickbooks/link`, { qbId: i.qbCustomerId }),
    onSuccess: (_r, i) => setDone(new Set(done).add(i.jobId)),
  });
  // Conflicts (two strong candidates) first, then the rest of the jobs, then customers and vendors.
  const notFound = qb.notFound.filter((u) => !only || u.kind === only).sort((a, b) => Number(b.reason.startsWith("Points to")) - Number(a.reason.startsWith("Points to")) || (a.kind === "job" ? 0 : 1) - (b.kind === "job" ? 0 : 1));
  const section = (title: string, n: number, body: React.ReactNode) =>
    n > 0 && (
      <details className="rounded-[var(--radius-control)] border border-line" open={n <= 20}>
        <summary className="cursor-pointer px-3 py-2 font-semibold">
          {title} ({n})
        </summary>
        <ul className="max-h-96 overflow-auto border-t border-line">{body}</ul>
      </details>
    );
  return (
    <Panel>
      <div id="needs-attention" className="scroll-mt-4" />
      <PanelHeader title="Needs attention" description="Nothing here changes on Apply. Fix these by hand when you have a minute; they drop off the next Sync." />
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        {link.error && <Alert>{errorMessage(link.error)}</Alert>}
        {qb.notFound.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {(["", "job", "customer", "vendor"] as const).map((k) => {
              const n = k ? qb.notFound.filter((u) => u.kind === k).length : qb.notFound.length;
              return (
                <button key={k} onClick={() => setOnly(k)} className={cx("rounded-full px-3 py-1 text-xs font-semibold", only === k ? "bg-ink text-white" : "bg-mist text-ink-soft hover:text-ink")}>
                  {k ? `${NOT_FOUND_LABEL[k][0]!.toUpperCase()}${NOT_FOUND_LABEL[k].slice(1)} ${n}` : `All not found ${n}`}
                </button>
              );
            })}
          </div>
        )}
        {section(
          only ? `Not found in QuickBooks: ${NOT_FOUND_LABEL[only]}` : "Not found in QuickBooks",
          notFound.length,
          notFound.map((u) => (
            <li key={`${u.kind}:${u.recordId}`} className="flex items-start gap-3 border-b border-mist px-3 py-2 last:border-0">
              <div className="min-w-0 flex-1">
                <a href={u.kind === "job" ? `/jobs/${u.recordId}` : `/contacts/${u.recordId}`} className="font-semibold hover:text-brand">
                  {u.label}
                </a>
                {u.detail && <div className="text-ink-soft">{u.detail}</div>}
                <div className={u.reason.startsWith("Points to") ? "text-wait" : "text-muted"}>{u.reason}</div>
              </div>
              {u.kind === "job" &&
                (done.has(u.recordId) ? (
                  <Badge tone="ok">Linked</Badge>
                ) : (
                  <Button className="shrink-0 px-3 py-1" onClick={() => setLinking(u)}>
                    Link…
                  </Button>
                ))}
            </li>
          )),
        )}
        {section(
          "JobNimbus invoices not matched in QuickBooks",
          qb.invoiceIssues.length,
          qb.invoiceIssues.map((i) => (
            <li key={i.invoiceId} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist px-3 py-2 last:border-0">
              <a href={`/jobs/${i.jobId}`} className="font-semibold hover:text-brand">
                {i.jobLabel}
              </a>
              <span>Invoice {i.number}</span>
              <span className="text-muted">{formatCents(i.totalCents)}</span>
              <span className="text-wait">{QB_INVOICE_ISSUE_LABELS[i.reason]}</span>
              <span className="min-w-0 flex-1 text-muted">
                {i.reason === "not_in_qb" ? "No QuickBooks invoice has this number." : i.reason === "amount" ? `QuickBooks has ${formatCents(i.qbTotalCents ?? 0)}.` : i.reason === "other_job" ? `QuickBooks has it on "${i.qbCustomerName ?? "?"}", not the job this one is linked to.` : `QuickBooks has it on "${i.qbCustomerName ?? "?"}".`}
              </span>
              {i.reason === "job_not_linked" &&
                i.qbCustomerId &&
                (done.has(i.jobId) ? (
                  <Badge tone="ok">Linked</Badge>
                ) : (
                  <Button className="px-3 py-1" busy={link.isPending && link.variables?.invoiceId === i.invoiceId} onClick={() => link.mutate(i)}>
                    Link job to “{i.qbCustomerName}”
                  </Button>
                ))}
            </li>
          )),
        )}
        {section(
          "Expenses on QuickBooks customers or jobs not linked here",
          qb.expenses.attention.length,
          qb.expenses.attention.map((a) => (
            <li key={`${a.qbType}:${a.qbId}`} className="flex flex-wrap gap-x-3 border-b border-mist px-3 py-2 last:border-0">
              <span className="font-semibold">{a.vendorName || QB_EXPENSE_TYPE_LABELS[a.qbType]}</span>
              <span className="text-muted">{[QB_EXPENSE_TYPE_LABELS[a.qbType], a.date && formatDate(a.date), a.job && `on ${a.job} here`, a.note].filter(Boolean).join(" · ")}</span>
              <span className="ml-auto">{formatCents(a.amountCents)}</span>
            </li>
          )),
        )}
      </div>
      {linking && (
        <LinkQbJobDialog
          jobId={linking.recordId}
          jobLabel={linking.label.split(" ").slice(0, 2).join(" ")}
          initial={linking.label.split(" ").slice(2).join(" ") || linking.label}
          onClose={() => setLinking(null)}
          onLinked={() => {
            setDone(new Set(done).add(linking.recordId));
            void qc.invalidateQueries({ queryKey: ["sync"] });
          }}
        />
      )}
    </Panel>
  );
}

function Applied({ state }: { state: SyncState }) {
  const a = state.applied!;
  const lines = [
    a.jobnimbus && `JobNimbus: ${a.jobnimbus.contacts} contacts, ${a.jobnimbus.jobs} jobs, ${a.jobnimbus.notes} notes, ${a.jobnimbus.tasks} tasks added; ${a.jobnimbus.invoices} invoices brought in or updated. Files: ${a.jobnimbus.files}`,
    `QuickBooks: ${a.links} ${a.links === 1 ? "link" : "links"} made.`,
    a.invoices && `Invoices: ${a.invoices.invoices.linked} matched to QuickBooks${a.invoices.invoices.mismatched ? `, ${a.invoices.invoices.mismatched} with amounts that differ` : ""}${a.invoices.invoices.merged ? `; ${a.invoices.invoices.merged} duplicate ${a.invoices.invoices.merged === 1 ? "copy" : "copies"} from QuickBooks merged into JobNimbus's invoice` : ""}.`,
    a.invoices?.voided && `Deleted in QuickBooks, voided here: ${a.invoices.voided.invoices} invoices, ${a.invoices.voided.payments} payments, ${a.invoices.voided.creditMemos} credit memos.`,
    a.expenses && `Expenses: ${expenseResultText(a.expenses)}.`,
  ].filter(Boolean);
  return (
    <Alert tone="ok">
      {lines.map((l) => (
        <div key={l as string}>{l}</div>
      ))}
    </Alert>
  );
}

/** "We're done with JobNimbus": Sync stops reading it and its import page goes away. */
function JobNimbusRetired({ status }: { status: SyncStatus }) {
  const { query, save } = useSettings();
  const qc = useQueryClient();
  if (!status.jobnimbus.setUp && !status.jobnimbus.retired) return null;
  const retired = query.data?.settings["jobnimbus.retired"] ?? status.jobnimbus.retired;
  return (
    <Panel>
      <PanelHeader title="JobNimbus" description="Once everyone works in the CRM, switch this on: Sync stops reading JobNimbus, and its import page goes away. What came over stays. You can switch it back off." />
      <div className="px-3 py-2 text-sm">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <Checkbox
          label="We're done with JobNimbus"
          checked={retired}
          disabled={save.isPending || !query.data}
          onChange={(v) => save.mutate({ "jobnimbus.retired": v }, { onSuccess: () => qc.invalidateQueries({ queryKey: ["sync"] }) })}
        />
      </div>
    </Panel>
  );
}
